'use client';

import { useEffect, useEffectEvent } from 'react';

import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { useSupabase } from './use-supabase';

/**
 * @name PRIVATE_PATH_PREFIXES
 * @description A list of private path prefixes
 */
const PRIVATE_PATH_PREFIXES = [
  '/home',
  '/admin',
  // 🔴 NICHT `/join`: Die Einladungsseite führt Abgemeldete selbst weiter
  // (Registrierung bzw. Anmeldung mit Einladung) oder meldet eine abgelaufene
  // Einladung. Stand `/join` hier, schickte dieser Hörer jeden Abgemeldeten
  // beim ersten Sitzungsereignis auf die Startseite — bevor die Seite ihn
  // weiterleiten konnte (gemessen 22.10.2026, Staging: `/join` → `/`).
  '/identities',
  '/update-password',
];

/**
 * @name AUTH_PATHS
 * @description A list of auth paths
 */
const AUTH_PATHS = ['/auth'];

/**
 * @name useAuthChangeListener
 * @param privatePathPrefixes - A list of private path prefixes
 * @param appHomePath - The path to redirect to when the user is signed out
 * @param onEvent - Callback function to be called when an auth event occurs
 */
export function useAuthChangeListener({
  privatePathPrefixes = PRIVATE_PATH_PREFIXES,
  onEvent,
}: {
  privatePathPrefixes?: string[];
  onEvent?: (event: AuthChangeEvent, user: Session | null) => void;
}) {
  const client = useSupabase();

  const setupAuthListener = useEffectEvent(() => {
    // don't run on the server
    if (typeof window === 'undefined') {
      return;
    }

    // keep this running for the whole session unless the component was unmounted
    return client.auth.onAuthStateChange((event, user) => {
      const pathName = window.location.pathname;

      if (onEvent) {
        onEvent(event, user);
      }

      // log user out if user is falsy
      // and if the current path is a private route
      const shouldRedirectUser =
        !user && isPrivateRoute(pathName, privatePathPrefixes);

      if (shouldRedirectUser) {
        // send user away when signed out
        window.location.assign('/');

        return;
      }

      // revalidate user session when user signs in or out
      if (event === 'SIGNED_OUT') {
        // sometimes Supabase sends SIGNED_OUT event
        // but in the auth path, so we ignore it
        if (AUTH_PATHS.some((path) => pathName.startsWith(path))) {
          return;
        }

        window.location.reload();
      }
    });
  });

  useEffect(() => {
    const listener = setupAuthListener();

    // destroy listener on un-mounts
    return () => {
      listener?.data.subscription.unsubscribe();
    };
  }, []);
}

/**
 * Determines if a given path is a private route.
 */
function isPrivateRoute(path: string, privatePathPrefixes: string[]) {
  return privatePathPrefixes.some((prefix) => path.startsWith(prefix));
}
